import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import { LogOut, Plus, Trash2, Package, Mail, FolderOpen, Settings, FileText, Lock, Edit, Download, Eye } from 'lucide-react';
import EmailSettingsTab from './EmailSettingsTab';
import ProductTemplatesTab from './ProductTemplatesTab';
import ChangePasswordTab from './ChangePasswordTab';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function AdminDashboard() {
  const navigate = useNavigate();
  const [offers, setOffers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [correspondence, setCorrespondence] = useState([]);
  const [loading, setLoading] = useState(false);

  // Dialog states
  const [categoryDialog, setCategoryDialog] = useState(false);
  const [productDialog, setProductDialog] = useState(false);
  const [categoryForm, setCategoryForm] = useState({ name: '', notification_email: '', description: '' });
  const [productForm, setProductForm] = useState({ name: '', category_id: '', specifications: '' });
  
  // Edit mode states
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingProduct, setEditingProduct] = useState(null);

  // Offer details dialog
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [offerDetailsDialog, setOfferDetailsDialog] = useState(false);
  
  // Product offers dialog
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productOffersDialog, setProductOffersDialog] = useState(false);
  const [productOffers, setProductOffers] = useState([]);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchData();
  }, []);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('admin_token');
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [offersRes, catsRes, prodsRes, corrRes] = await Promise.all([
        axios.get(`${API}/admin/offers`, getAuthHeaders()),
        axios.get(`${API}/categories`),
        axios.get(`${API}/products`),
        axios.get(`${API}/admin/correspondence`, getAuthHeaders()),
      ]);
      setOffers(offersRes.data);
      setCategories(catsRes.data);
      setProducts(prodsRes.data);
      setCorrespondence(corrRes.data);
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/admin/login');
      } else {
        toast.error('Eroare la încărcarea datelor');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_username');
    toast.success('Deconectat cu succes');
    navigate('/admin/login');
  };

  const handleCreateCategory = async () => {
    try {
      if (editingCategory) {
        // Update existing category
        await axios.put(`${API}/admin/categories/${editingCategory.id}`, categoryForm, getAuthHeaders());
        toast.success('Categorie actualizată cu succes');
      } else {
        // Create new category
        await axios.post(`${API}/admin/categories`, categoryForm, getAuthHeaders());
        toast.success('Categorie creată cu succes');
      }
      setCategoryDialog(false);
      setCategoryForm({ name: '', notification_email: '', description: '' });
      setEditingCategory(null);
      fetchData();
    } catch (error) {
      toast.error(editingCategory ? 'Eroare la actualizarea categoriei' : 'Eroare la crearea categoriei');
    }
  };

  const handleEditCategory = (category) => {
    setCategoryForm({
      name: category.name,
      notification_email: category.notification_email,
      description: category.description || ''
    });
    setEditingCategory(category);
    setCategoryDialog(true);
  };

  const handleCancelEditCategory = (open) => {
    if (!open) {
      // Only reset when closing
      setCategoryDialog(false);
      setCategoryForm({ name: '', notification_email: '', description: '' });
      setEditingCategory(null);
    } else {
      setCategoryDialog(true);
    }
  };

  const handleDeleteCategory = async (id) => {
    if (!window.confirm('Sigur doriți să ștergeți această categorie?')) return;
    try {
      await axios.delete(`${API}/admin/categories/${id}`, getAuthHeaders());
      toast.success('Categorie ștearsă');
      fetchData();
    } catch (error) {
      toast.error('Eroare la ștergerea categoriei');
    }
  };

  const handleCreateProduct = async () => {
    try {
      if (editingProduct) {
        // Update existing product
        await axios.put(`${API}/admin/products/${editingProduct.id}`, productForm, getAuthHeaders());
        toast.success('Produs actualizat cu succes');
      } else {
        // Create new product
        await axios.post(`${API}/admin/products`, productForm, getAuthHeaders());
        toast.success('Produs creat cu succes');
      }
      setProductDialog(false);
      setProductForm({ name: '', category_id: '', specifications: '' });
      setEditingProduct(null);
      fetchData();
    } catch (error) {
      toast.error(editingProduct ? 'Eroare la actualizarea produsului' : 'Eroare la crearea produsului');
    }
  };

  const handleEditProduct = (product) => {
    setProductForm({
      name: product.name,
      category_id: product.category_id,
      specifications: product.specifications
    });
    setEditingProduct(product);
    setProductDialog(true);
  };

  const handleCancelEditProduct = (open) => {
    if (!open) {
      // Only reset when closing
      setProductDialog(false);
      setProductForm({ name: '', category_id: '', specifications: '' });
      setEditingProduct(null);
    } else {
      setProductDialog(true);
    }
  };

  const handleDeleteProduct = async (id) => {
    if (!window.confirm('Sigur doriți să ștergeți acest produs?')) return;
    try {
      await axios.delete(`${API}/admin/products/${id}`, getAuthHeaders());
      toast.success('Produs șters');
      fetchData();
    } catch (error) {
      toast.error('Eroare la ștergerea produsului');
    }
  };

  const viewOfferDetails = (offer) => {
    setSelectedOffer(offer);
    setOfferDetailsDialog(true);
  };

  const handleDeleteOffer = async (offerId) => {
    if (!window.confirm('Sigur doriți să ștergeți această ofertă? Această acțiune nu poate fi anulată.')) return;
    try {
      await axios.delete(`${API}/admin/offers/${offerId}`, getAuthHeaders());
      toast.success('Ofertă ștearsă cu succes');
      fetchData();
    } catch (error) {
      toast.error('Eroare la ștergerea ofertei');
    }
  };

  const handleDownloadJSON = async (offerId) => {
    try {
      const response = await axios.get(`${API}/admin/offers/${offerId}/ai-data`, getAuthHeaders());
      const jsonData = JSON.stringify(response.data, null, 2);
      
      // Create blob and download
      const blob = new Blob([jsonData], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `oferta_${offerId}_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Fișier JSON descărcat cu succes');
    } catch (error) {
      toast.error('Eroare la descărcarea fișierului JSON');
    }
  };

  const handleViewProductOffers = async (product) => {
    try {
      const response = await axios.get(`${API}/admin/products/${product.id}/offers`, getAuthHeaders());
      setSelectedProduct(product);
      setProductOffers(response.data.offers || []);
      setProductOffersDialog(true);
    } catch (error) {
      toast.error('Eroare la încărcarea ofertelor pentru acest produs');
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#f5f5f5' }}>
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Package className="w-8 h-8 text-gray-700" />
            <div>
              <h1 className="text-xl font-semibold text-gray-800">Admin Dashboard</h1>
              <p className="text-sm text-gray-500">Sistem Gestionare Oferte</p>
            </div>
          </div>
          <Button variant="ghost" onClick={handleLogout} data-testid="logout-button">
            <LogOut className="mr-2 h-4 w-4" />
            Deconectare
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <Tabs defaultValue="offers" className="w-full">
          <TabsList className="bg-white border border-gray-200 p-1" data-testid="admin-tabs">
            <TabsTrigger value="offers" data-testid="tab-offers">
              Oferte Primite
            </TabsTrigger>
            <TabsTrigger value="categories" data-testid="tab-categories">
              Categorii
            </TabsTrigger>
            <TabsTrigger value="products" data-testid="tab-products">
              Produse
            </TabsTrigger>
            <TabsTrigger value="correspondence" data-testid="tab-correspondence">
              Corespondență
            </TabsTrigger>
            <TabsTrigger value="templates" data-testid="tab-templates">
              <FileText className="w-4 h-4 mr-2" />
              Specificațiile Noastre
            </TabsTrigger>
            <TabsTrigger value="settings" data-testid="tab-settings">
              <Settings className="w-4 h-4 mr-2" />
              Setări Email
            </TabsTrigger>
            <TabsTrigger value="password" data-testid="tab-password">
              <Lock className="w-4 h-4 mr-2" />
              Schimbă Parola
            </TabsTrigger>
          </TabsList>

          {/* Offers Tab */}
          <TabsContent value="offers" className="mt-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <FolderOpen className="w-5 h-5" />
                Oferte Primite ({offers.length})
              </h2>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Data</TableHead>
                      <TableHead>Companie</TableHead>
                      <TableHead>Produse</TableHead>
                      <TableHead>Contact</TableHead>
                      <TableHead>Acțiuni</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {offers.map((offer) => (
                      <TableRow key={offer.id} data-testid={`offer-row-${offer.id}`}>
                        <TableCell>
                          {new Date(offer.created_at).toLocaleDateString('ro-RO')}
                        </TableCell>
                        <TableCell>
                          <div className="font-medium">{offer.company_name}</div>
                          <div className="text-xs text-gray-500">{offer.fiscal_code}</div>
                        </TableCell>
                        <TableCell>
                          <div className="font-medium">
                            {offer.products?.length || 0} produs(e)
                          </div>
                          <div className="text-xs text-gray-500">
                            {offer.products?.[0]?.category_name}
                            {offer.products?.length > 1 && ' + altele'}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">{offer.contact_name}</div>
                          <div className="text-xs text-gray-500">{offer.contact_email}</div>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => viewOfferDetails(offer)}
                            >
                              Detalii
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDownloadJSON(offer.id)}
                              title="Descarcă JSON"
                            >
                              <Download className="h-4 w-4 text-blue-500" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteOffer(offer.id)}
                              title="Șterge oferta"
                            >
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          </TabsContent>

          {/* Categories Tab */}
          <TabsContent value="categories" className="mt-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold">Categorii ({categories.length})</h2>
                <Dialog open={categoryDialog} onOpenChange={handleCancelEditCategory}>
                  <DialogTrigger asChild>
                    <Button data-testid="add-category-button" style={{ backgroundColor: '#1a73e8' }}>
                      <Plus className="mr-2 h-4 w-4" />
                      Adaugă Categorie
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>
                        {editingCategory ? 'Editează Categorie' : 'Categorie Nouă'}
                      </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                      <div>
                        <Label>Nume Categorie</Label>
                        <Input
                          value={categoryForm.name}
                          onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                          placeholder="Electronice"
                          data-testid="category-name-input"
                        />
                      </div>
                      <div>
                        <Label>Email Notificări</Label>
                        <Input
                          type="email"
                          value={categoryForm.notification_email}
                          onChange={(e) =>
                            setCategoryForm({ ...categoryForm, notification_email: e.target.value })
                          }
                          placeholder="electronice@pirifan.ro"
                          data-testid="category-email-input"
                        />
                      </div>
                      <div>
                        <Label>Descriere (opțional)</Label>
                        <Textarea
                          value={categoryForm.description}
                          onChange={(e) =>
                            setCategoryForm({ ...categoryForm, description: e.target.value })
                          }
                          placeholder="Descriere categorie..."
                          data-testid="category-description-input"
                        />
                      </div>
                      <div className="flex gap-2">
                        <Button
                          onClick={handleCreateCategory}
                          className="flex-1"
                          data-testid="save-category-button"
                        >
                          {editingCategory ? 'Actualizează' : 'Salvează'}
                        </Button>
                        {editingCategory && (
                          <Button
                            variant="outline"
                            onClick={handleCancelEditCategory}
                            data-testid="cancel-edit-category-button"
                          >
                            Anulează
                          </Button>
                        )}
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nume</TableHead>
                    <TableHead>Email Notificări</TableHead>
                    <TableHead>Descriere</TableHead>
                    <TableHead className="text-right">Acțiuni</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {categories.map((cat) => (
                    <TableRow key={cat.id}>
                      <TableCell className="font-medium">{cat.name}</TableCell>
                      <TableCell>{cat.notification_email}</TableCell>
                      <TableCell className="text-sm text-gray-500">{cat.description || '-'}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex gap-1 justify-end">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditCategory(cat)}
                            data-testid={`edit-category-${cat.id}`}
                          >
                            <Edit className="h-4 w-4 text-blue-500" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteCategory(cat.id)}
                            data-testid={`delete-category-${cat.id}`}
                          >
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* Products Tab */}
          <TabsContent value="products" className="mt-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold">Produse ({products.length})</h2>
                <Dialog open={productDialog} onOpenChange={handleCancelEditProduct}>
                  <DialogTrigger asChild>
                    <Button data-testid="add-product-button" style={{ backgroundColor: '#1a73e8' }}>
                      <Plus className="mr-2 h-4 w-4" />
                      Adaugă Produs
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>
                        {editingProduct ? 'Editează Produs' : 'Produs Nou'}
                      </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                      <div>
                        <Label>Nume Produs</Label>
                        <Input
                          value={productForm.name}
                          onChange={(e) => setProductForm({ ...productForm, name: e.target.value })}
                          placeholder="Laptop Dell XPS"
                          data-testid="product-name-input"
                        />
                      </div>
                      <div>
                        <Label>Categorie</Label>
                        <select
                          className="w-full border border-gray-300 rounded-md p-2"
                          value={productForm.category_id}
                          onChange={(e) =>
                            setProductForm({ ...productForm, category_id: e.target.value })
                          }
                          data-testid="product-category-select"
                        >
                          <option value="">Selectați categoria</option>
                          {categories.map((cat) => (
                            <option key={cat.id} value={cat.id}>
                              {cat.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <Label>Specificații</Label>
                        <Textarea
                          value={productForm.specifications}
                          onChange={(e) =>
                            setProductForm({ ...productForm, specifications: e.target.value })
                          }
                          placeholder="Specificații produs..."
                          data-testid="product-specifications-input"
                        />
                      </div>
                      <div className="flex gap-2">
                        <Button
                          onClick={handleCreateProduct}
                          className="flex-1"
                          data-testid="save-product-button"
                        >
                          {editingProduct ? 'Actualizează' : 'Salvează'}
                        </Button>
                        {editingProduct && (
                          <Button
                            variant="outline"
                            onClick={handleCancelEditProduct}
                            data-testid="cancel-edit-product-button"
                          >
                            Anulează
                          </Button>
                        )}
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nume</TableHead>
                    <TableHead>Categorie</TableHead>
                    <TableHead>Specificații</TableHead>
                    <TableHead className="text-right">Acțiuni</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {products.map((prod) => {
                    const category = categories.find((c) => c.id === prod.category_id);
                    return (
                      <TableRow key={prod.id}>
                        <TableCell className="font-medium">{prod.name}</TableCell>
                        <TableCell>{category?.name || '-'}</TableCell>
                        <TableCell className="text-sm text-gray-500">
                          {prod.specifications.substring(0, 50)}...
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex gap-1 justify-end">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleViewProductOffers(prod)}
                              data-testid={`view-offers-${prod.id}`}
                              title="Vezi ofertele pentru acest produs"
                            >
                              <Eye className="h-4 w-4 text-green-500" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditProduct(prod)}
                              data-testid={`edit-product-${prod.id}`}
                            >
                              <Edit className="h-4 w-4 text-blue-500" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteProduct(prod.id)}
                              data-testid={`delete-product-${prod.id}`}
                            >
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* Correspondence Tab */}
          <TabsContent value="correspondence" className="mt-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Mail className="w-5 h-5" />
                Corespondență Email ({correspondence.length})
              </h2>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Data</TableHead>
                    <TableHead>De la</TableHead>
                    <TableHead>Către</TableHead>
                    <TableHead>Subiect</TableHead>
                    <TableHead>Detalii Ofertă</TableHead>
                    <TableHead>Acțiuni</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {correspondence.map((corr) => {
                    const relatedOffer = offers.find(o => o.id === corr.offer_id);
                    return (
                      <TableRow key={corr.id}>
                        <TableCell className="text-sm">
                          {new Date(corr.timestamp).toLocaleString('ro-RO')}
                        </TableCell>
                        <TableCell className="text-sm">{corr.from_email}</TableCell>
                        <TableCell className="text-sm">{corr.to_email}</TableCell>
                        <TableCell className="font-medium">{corr.subject}</TableCell>
                        <TableCell>
                          {relatedOffer ? (
                            <div className="text-sm">
                              <div className="font-medium text-gray-700">
                                {relatedOffer.products?.length || 0} produs(e):
                              </div>
                              {relatedOffer.products?.slice(0, 2).map((prod, idx) => (
                                <div key={idx} className="text-xs text-gray-600 mt-1">
                                  • {prod.product_name} - {prod.price}
                                </div>
                              ))}
                              {relatedOffer.products?.length > 2 && (
                                <div className="text-xs text-gray-500 italic">
                                  + {relatedOffer.products.length - 2} mai multe
                                </div>
                              )}
                            </div>
                          ) : (
                            <span className="text-xs text-gray-400">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {corr.offer_id && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDownloadJSON(corr.offer_id)}
                              title="Descarcă JSON"
                            >
                              <Download className="h-4 w-4 text-blue-500" />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* Product Templates Tab */}
          <TabsContent value="templates" className="mt-6">
            <ProductTemplatesTab
              getAuthHeaders={getAuthHeaders}
              categories={categories}
              products={products}
            />
          </TabsContent>

          {/* Email Settings Tab */}
          <TabsContent value="settings" className="mt-6">
            <EmailSettingsTab getAuthHeaders={getAuthHeaders} />
          </TabsContent>

          {/* Change Password Tab */}
          <TabsContent value="password" className="mt-6">
            <ChangePasswordTab getAuthHeaders={getAuthHeaders} />
          </TabsContent>
        </Tabs>
      </div>

      {/* Offer Details Dialog */}
      <Dialog open={offerDetailsDialog} onOpenChange={setOfferDetailsDialog}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Detalii Ofertă</DialogTitle>
          </DialogHeader>
          {selectedOffer && (
            <div className="space-y-6 mt-4">
              {/* Company Info */}
              <div>
                <h3 className="font-semibold text-gray-700 mb-2">Informații Companie</h3>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-gray-500">Nume:</span> {selectedOffer.company_name}
                  </div>
                  <div>
                    <span className="text-gray-500">Cod Fiscal:</span> {selectedOffer.fiscal_code}
                  </div>
                  <div>
                    <span className="text-gray-500">Localitate:</span> {selectedOffer.locality}
                  </div>
                </div>
              </div>

              {/* Contact Info */}
              <div>
                <h3 className="font-semibold text-gray-700 mb-2">Persoană Contact</h3>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-gray-500">Nume:</span> {selectedOffer.contact_name}
                  </div>
                  <div>
                    <span className="text-gray-500">Email:</span> {selectedOffer.contact_email}
                  </div>
                  <div>
                    <span className="text-gray-500">Telefon:</span> {selectedOffer.contact_phone}
                  </div>
                </div>
              </div>

              {/* Products */}
              <div>
                <h3 className="font-semibold text-gray-700 mb-2">
                  Produse ({selectedOffer.products?.length || 0})
                </h3>
                <div className="space-y-4">
                  {selectedOffer.products?.map((product, idx) => (
                    <div key={idx} className="border border-gray-200 rounded p-4 bg-gray-50">
                      <div className="font-medium mb-2">
                        {idx + 1}. {product.product_name}
                      </div>
                      <div className="text-sm space-y-1">
                        <div>
                          <span className="text-gray-500">Categorie:</span> {product.category_name}
                        </div>
                        <div>
                          <span className="text-gray-500">Specificații:</span> {product.specifications}
                        </div>
                        <div>
                          <span className="text-gray-500">Cantitate minimă:</span> {product.quantity}
                        </div>
                        <div>
                          <span className="text-gray-500">Preț:</span> {product.price}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Payment Terms */}
              <div>
                <h3 className="font-semibold text-gray-700 mb-2">Condiții Plată</h3>
                <div className="text-sm">{selectedOffer.payment_terms}</div>
              </div>

              {/* Date */}
              <div>
                <h3 className="font-semibold text-gray-700 mb-2">Data Ofertă</h3>
                <div className="text-sm">
                  {new Date(selectedOffer.created_at).toLocaleString('ro-RO')}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Product Offers Dialog */}
      <Dialog open={productOffersDialog} onOpenChange={setProductOffersDialog}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Oferte pentru: {selectedProduct?.name}</DialogTitle>
          </DialogHeader>
          {selectedProduct && (
            <div className="mt-4">
              <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                <div className="text-sm text-gray-600">
                  <strong>Categorie:</strong> {categories.find(c => c.id === selectedProduct.category_id)?.name || '-'}
                </div>
                <div className="text-sm text-gray-600 mt-1">
                  <strong>Total oferte primite:</strong> {productOffers.length}
                </div>
              </div>

              {productOffers.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  Nu există oferte pentru acest produs
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Data</TableHead>
                      <TableHead>Ofertant</TableHead>
                      <TableHead>Contact</TableHead>
                      <TableHead>Preț</TableHead>
                      <TableHead>Cantitate</TableHead>
                      <TableHead>Termen Plată</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {productOffers.map((offer, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="text-sm">
                          {new Date(offer.created_at).toLocaleDateString('ro-RO')}
                        </TableCell>
                        <TableCell>
                          <div className="font-medium">{offer.company_name}</div>
                          <div className="text-xs text-gray-500">{offer.contact_name}</div>
                        </TableCell>
                        <TableCell className="text-sm">
                          {offer.contact_email}
                        </TableCell>
                        <TableCell className="font-medium text-green-600">
                          {offer.price}
                        </TableCell>
                        <TableCell className="text-sm">
                          {offer.quantity}
                        </TableCell>
                        <TableCell className="text-sm">
                          {offer.payment_terms}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

    </div>
  );
}

export default AdminDashboard;